import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { toast } from "sonner";
import { Download, Loader2, Lock, RefreshCw, ScrollText, Clock, FileX2, MessagesSquare, History } from "lucide-react";
import { ReportPaper, tokFor } from "@/components/bi/ReportPreview";
import { PublicReportCommentsPanel } from "@/components/bi/ReportCommentsPanel";

const API = process.env.REACT_APP_BACKEND_URL;
const pub = axios.create({ baseURL: `${API}/api/public/report` });
const FMT_LABELS = [["pdf", "PDF"], ["xlsx", "Excel"], ["docx", "Word"], ["html", "HTML"]];

function Screen({ icon: Icon, title, children }) {
  return (
    <div className="min-h-screen flex items-center justify-center p-6" style={{ background: "var(--canvas)" }}>
      <div className="text-center max-w-sm w-full">
        <Icon className="w-10 h-10 mx-auto text-[color:var(--ink-3)]" />
        <div className="mt-3 font-display text-lg text-[color:var(--ink)]">{title}</div>
        {children}
      </div>
    </div>
  );
}

export default function ReportViewer() {
  const { token } = useParams();
  const [gate, setGate] = useState(null);
  const [access, setAccess] = useState(() => sessionStorage.getItem(`eip_rep_access_${token}`) || "");
  const [pw, setPw] = useState("");
  const [pwErr, setPwErr] = useState("");
  const [unlocking, setUnlocking] = useState(false);
  const [report, setReport] = useState(null);
  const [preview, setPreview] = useState({});
  const [paramValues, setParamValues] = useState({});
  const [running, setRunning] = useState(false);
  const [downloading, setDownloading] = useState(null);
  const [commentsOpen, setCommentsOpen] = useState(false);
  const [versions, setVersions] = useState([]);          // shared version history (may be empty)
  const [versionIndex, setVersionIndex] = useState(null); // null = live, integer = pinned version

  const headers = useMemo(() => (access ? { "X-Share-Access": access } : {}), [access]);

  useEffect(() => { pub.get(`/${token}/gate`).then(({ data }) => setGate(data)).catch(() => setGate({ found: false })); }, [token]);

  const run = useCallback(async (params, hdrs, vIdx) => {
    setRunning(true);
    try {
      const url = vIdx != null ? `/${token}/run?v=${vIdx}` : `/${token}/run`;
      const { data } = await pub.post(url, { params }, { headers: hdrs });
      setPreview(data.elements || {});
    } catch { /* keep last */ }
    setRunning(false);
  }, [token]);

  const loadReport = useCallback(async (hdrs, vIdx) => {
    try {
      const url = vIdx != null ? `/${token}?v=${vIdx}` : `/${token}`;
      const { data } = await pub.get(url, { headers: hdrs });
      setReport(data);
      const defs = {};
      (data.parameters || []).forEach((p) => { if (p.name) defs[p.name] = p.default ?? ""; });
      setParamValues(defs);
      run(defs, hdrs, vIdx);
    } catch (e) {
      if (e?.response?.status === 401) {
        sessionStorage.removeItem(`eip_rep_access_${token}`);
        setAccess("");
      }
    }
  }, [token, run]);

  useEffect(() => {
    if (!gate?.found || gate.expired) return;
    if (gate.password_required && !access) return;
    loadReport(access ? { "X-Share-Access": access } : {}, versionIndex);
  }, [gate, access, loadReport, versionIndex]);

  // Fetch version list (only shown when the shared report has 1+ versions)
  useEffect(() => {
    if (!gate?.found || (gate.password_required && !access)) return;
    pub.get(`/${token}/versions`, { headers: access ? { "X-Share-Access": access } : {} })
      .then(({ data }) => setVersions(data || []))
      .catch(() => setVersions([]));
  }, [gate, access, token]);

  const unlock = async () => {
    setUnlocking(true);
    setPwErr("");
    try {
      const { data } = await pub.post("/verify-password", { token, password: pw });
      sessionStorage.setItem(`eip_rep_access_${token}`, data.access_token);
      setAccess(data.access_token);
    } catch (e) {
      setPwErr(e?.response?.status === 410 ? "This link has expired" : "Incorrect password");
    }
    setUnlocking(false);
  };

  const download = async (fmt) => {
    setDownloading(fmt);
    try {
      const vSuffix = versionIndex != null ? `&v=${versionIndex}` : "";
      const { data } = await pub.post(`/${token}/render?fmt=${fmt}${vSuffix}`, { params: paramValues }, { headers, responseType: "blob" });
      const url = URL.createObjectURL(data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${(report?.name || "report").replace(/\s+/g, "_")}${versionIndex != null ? `_v${versionIndex + 1}` : ""}.${fmt}`;
      a.click();
      URL.revokeObjectURL(url);
    } catch { toast.error("Download failed"); }
    setDownloading(null);
  };

  if (gate === null) {
    return <div className="min-h-screen flex items-center justify-center text-sm text-[color:var(--ink-3)]" style={{ background: "var(--canvas)" }}><Loader2 className="w-5 h-5 animate-spin mr-2" />Loading…</div>;
  }
  if (!gate.found) {
    return <Screen icon={FileX2} title="Report not found"><p className="text-sm text-[color:var(--ink-3)] mt-1">This link is invalid or sharing was turned off.</p></Screen>;
  }
  if (gate.expired) {
    return <Screen icon={Clock} title="This link has expired"><p className="text-sm text-[color:var(--ink-3)] mt-1">Ask the report owner for a fresh link.</p></Screen>;
  }
  if (gate.password_required && !access) {
    return (
      <Screen icon={Lock} title={gate.name || "Protected report"}>
        <p className="text-sm text-[color:var(--ink-3)] mt-1 mb-3">This report is password protected.</p>
        <div data-testid="report-password-gate" className="flex gap-2">
          <Input data-testid="report-password-input" type="password" value={pw} onChange={(e) => setPw(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && unlock()} placeholder="Password" className="bg-[color:var(--surface)]" />
          <Button data-testid="report-password-submit" onClick={unlock} disabled={unlocking} className="bg-[color:var(--brand-ocean)] text-white">
            {unlocking ? <Loader2 className="w-4 h-4 animate-spin" /> : "Open"}
          </Button>
        </div>
        {pwErr && <div data-testid="report-password-error" className="text-xs text-[color:var(--danger)] mt-2">{pwErr}</div>}
      </Screen>
    );
  }
  if (!report) {
    return <div className="min-h-screen flex items-center justify-center text-sm text-[color:var(--ink-3)]" style={{ background: "var(--canvas)" }}><Loader2 className="w-5 h-5 animate-spin mr-2" />Loading report…</div>;
  }

  const tok = tokFor(report);
  return (
    <div className="min-h-screen flex flex-col" style={{ background: "var(--canvas)", "--rd-primary": report.branding?.primary_color || report.theme?.primary || "#2B4EFF" }}>
      <header className="flex items-center gap-2 px-4 py-2 border-b border-[color:var(--glass-border)] bg-[color:var(--surface)] sticky top-0 z-20">
        <ScrollText className="w-4 h-4 text-[color:var(--brand-ocean)]" />
        <span data-testid="report-viewer-name" className="font-medium text-[color:var(--ink)] truncate">{report.name}</span>
        {versionIndex != null && (
          <span data-testid="report-viewer-version-badge" className="text-[10px] px-1.5 py-0.5 rounded-full bg-[color:var(--brand-mint)]/15 text-[color:var(--brand-ocean)] font-semibold">
            {versions[versions.length - 1 - versionIndex]?.label
              || `Version ${versionIndex + 1}`}
          </span>
        )}
        <span className="flex-1" />
        {versions.length > 0 && (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button data-testid="report-viewer-versions" variant="outline" size="sm" className="h-8 text-xs bg-[color:var(--surface-2)]">
                <History className="w-3.5 h-3.5 mr-1" />
                {versionIndex != null ? `v${versionIndex + 1}` : "Latest"}
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="max-h-[320px] overflow-y-auto">
              <DropdownMenuItem data-testid="report-viewer-version-latest" onClick={() => setVersionIndex(null)}>
                <span className={versionIndex == null ? "font-semibold text-[color:var(--brand-ocean)]" : ""}>Latest (live)</span>
              </DropdownMenuItem>
              {versions.slice().reverse().map((v) => (
                <DropdownMenuItem key={v.index} data-testid={`report-viewer-version-${v.index}`}
                  onClick={() => setVersionIndex(v.index)}>
                  <div className="flex flex-col">
                    <span className={versionIndex === v.index ? "font-semibold text-[color:var(--brand-ocean)]" : ""}>
                      {v.label || v.name || `Version ${v.index + 1}`}
                    </span>
                    <span className="text-[10px] text-[color:var(--ink-3)]">
                      {v.saved_at ? new Date(v.saved_at).toLocaleString() : ""} · {v.saved_by || ""}
                    </span>
                  </div>
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>
        )}
        <Button variant="outline" size="sm" onClick={() => setCommentsOpen(true)} className="h-8 text-xs bg-[color:var(--surface-2)]" data-testid="report-viewer-comments">
          <MessagesSquare className="w-3.5 h-3.5 mr-1" />{versionIndex != null ? "Approve" : "Comments"}
        </Button>
        <Button variant="outline" size="sm" onClick={() => run(paramValues, headers, versionIndex)} disabled={running} className="h-8 text-xs bg-[color:var(--surface-2)]" data-testid="report-viewer-refresh">
          {running ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5 mr-1" />}Refresh
        </Button>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button data-testid="report-viewer-download" size="sm" className="h-8 text-xs bg-[color:var(--brand-ocean)] text-white">
              {downloading ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <Download className="w-3.5 h-3.5 mr-1" />}Download
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            {FMT_LABELS.map(([v, l]) => <DropdownMenuItem key={v} data-testid={`report-viewer-dl-${v}`} onClick={() => download(v)}>{l}</DropdownMenuItem>)}
          </DropdownMenuContent>
        </DropdownMenu>
      </header>
      <main className="flex-1 overflow-auto p-6">
        {(report.parameters || []).length > 0 && (
          <div data-testid="report-viewer-params" className="flex items-center gap-2 flex-wrap mb-4 max-w-[920px] mx-auto">
            {report.parameters.map((p) => (
              <label key={p.id || p.name} className="flex items-center gap-1.5 text-xs text-[color:var(--ink-2)] glass-card pl-3 pr-1.5 py-1 rounded-xl">
                {p.name}
                <Input data-testid={`report-viewer-param-${p.name}`} value={paramValues[p.name] ?? ""}
                  onChange={(e) => setParamValues((v) => ({ ...v, [p.name]: e.target.value }))}
                  placeholder={p.default || "any"} className="h-7 w-32 text-xs bg-[color:var(--surface)]" />
              </label>
            ))}
            <Button size="sm" variant="outline" onClick={() => run(paramValues, headers)} className="h-7 text-xs bg-[color:var(--surface-2)]" data-testid="report-viewer-params-apply">Apply</Button>
          </div>
        )}
        <ReportPaper report={report} tok={tok} preview={preview} />
        <div className="text-center text-[10px] text-[color:var(--ink-3)] mt-3">
          {versionIndex != null ? "Historical snapshot · data was captured at save time" : "Shared report · data reflects the owner's live datasets"}
        </div>
      </main>
      <PublicReportCommentsPanel open={commentsOpen} onOpenChange={setCommentsOpen} token={token} accessToken={access} versionIndex={versionIndex} />
    </div>
  );
}
